/**
 * The engine driver (fix D / R4): it turns the BROWSED position into the props
 * `@/Components/StockfishEngine` takes, and the engine's standard-UCI answers
 * into `analysisStore`.
 *
 * It is only ever called from `EngineHost`, which the rail mounts ONLY while
 * `analysisStore.on` — so "stop the engine" is an unmount, which is what
 * actually terminates the worker (`StockfishEngine`'s `engineOn` lifecycle
 * effect), and a live game has no engine in its tree at all.
 *
 * TWO conversions happen here, at the edge, so the three readers share one
 * convention:
 *  - SIDE OF THE SCORE. Standard UCI reports the score from the SIDE TO MOVE's
 *    point of view (`parser.ts`'s note); everything downstream wants white's, so
 *    `baseTurn === 'b'` flips the sign once, here.
 *  - STALENESS. The component passes the FEN it actually searched as the last
 *    `onResult` argument; a line whose `searchedFen` is not the position the
 *    store is on is dropped (`analysisStore.applyResult`). Without it, browsing
 *    back two plies shows the previous position's evaluation for ~1.5 s.
 *
 * FIX F-D / D3: in assist mode (the live eval bar of an assisted game) the
 * search ignores `viewPly` AND the branch — there is no notation browsing or
 * branching while a game of mine is live, so the engine always follows the
 * LIVE position, shallower and faster than self-analysis's 3-line readout
 * (the old screen's own `StockfishEngine` ran depth 32 / 1 line continuously,
 * L5783-5790; 20 / 1 / 2.5s is this engine's balance for running every move
 * rather than once after the game).
 *
 * FIX L / L1 (2026-10-06): assist mode no longer hands the engine the LIVE
 * position directly — it hands it the HEAD of `analysisStore.assistQueue`
 * (falling back to the live position only while the queue is empty), and
 * offers every live position to the queue instead of jumping straight to
 * it. A WATCHDOG settles the head on its own if `bestmove` never arrives
 * (chiefly a crashed/re-initialising worker — fix U below now keeps a
 * mated/stalemated head from ever being searched, so that case no longer
 * relies on the watchdog, which still backstops it regardless) — ARMED
 * ONLY while `engineStatus.status === 'ready'` (review fix, 2026-10-06): a
 * cold worker's own init + uci/isready handshake can outlast `budgetMs +
 * ASSIST_SETTLE_GRACE_MS`, and arming it from mount would then settle the
 * very FIRST head with no `lines` at all — a chip lost to boot time, not a
 * stuck search. The one remaining gap: a worker that never reaches 'ready'
 * (`engineStatus` stuck at 'loading', or 'unsupported'/'error') never arms
 * the watchdog at all — no chips land, but the queue still advances on its
 * own (every live position keeps being offered; nothing settles until the
 * worker recovers or the game ends), same as decision 2's "no local
 * classifier, no silent partial state" either way.
 *
 * FIX U (2026-10-06): `searchEnabled` is false for a terminal position —
 * `analysisStore`'s own `terminalVerdict(fen)`, computed again here (same
 * pure helper, same fen) rather than read off the store, so it is correct on
 * the very FIRST render of a new `fen` rather than one tick behind the
 * store's own effect. `EngineHost` forwards it straight to `StockfishEngine`,
 * which stops/never starts a search while keeping the worker warm — stepping
 * back out of the terminal position is just another `fen`, and the search
 * resumes without paying init again.
 */
import {useCallback, useEffect, useMemo} from 'react';

import {useLiveGame} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';
import {fenAtPly, selectBoardSource} from '@/Store/liveGame/selectors';

import {watchBoardPropsAtPly} from '../../stage/BoardFrame/watchBoard';
import {useV2Ui} from '../../theme/useV2Ui';
import {terminalVerdict} from './branch';
import {useEngineStatus} from './engineStatus';
import {
  ANALYSIS_DEPTH,
  ANALYSIS_LINES,
  ANALYSIS_MOVETIME_MS,
  ASSIST_DEPTH,
  ASSIST_LINES,
  ASSIST_MOVETIME_MS,
  ASSIST_SETTLE_GRACE_MS,
  selectBranchTip,
  useAnalysis,
} from './analysisStore';

// fix L / L1: the three ASSIST_* constants used here moved to
// `analysisStore.ts`, next to `ANALYSIS_*` — re-exported so any existing
// import of them from this file keeps working.
export {ASSIST_DEPTH, ASSIST_LINES, ASSIST_MOVETIME_MS} from './analysisStore';

/**
 * The position under the engine: the ply the notation is BROWSING, on whichever
 * game owns the board. `fenAtPly` is the core's own memoised replay, so stepping
 * through a game does not re-run chess.js per clock tick.
 */
export const analysisFenAtPly = (
  s: LiveGameState,
  viewPly: number | null,
): string =>
  selectBoardSource(s) === 'watch'
    ? watchBoardPropsAtPly(s, viewPly).position
    : fenAtPly(s.game.startFen, s.game.moves, viewPly ?? s.game.moves.length);

/** how many plies deep the analysed position is (the `evalsByPly` key). */
export const analysisPly = (
  s: LiveGameState,
  viewPly: number | null,
): number => {
  const total =
    selectBoardSource(s) === 'watch'
      ? s.watch.moves.length
      : s.game.moves.length;
  return viewPly === null ? total : Math.min(viewPly, total);
};

/**
 * The main-line SAN moves of whichever game owns the board — what
 * `analysisMove.ts`'s `isMainlineNext` checks a branch's first move against
 * (fix F-C / C2). The watch/live split mirrors `analysisFenAtPly`'s own.
 */
export const analysisMoves = (s: LiveGameState): string[] =>
  selectBoardSource(s) === 'watch' ? s.watch.moves : s.game.moves;

export type EngineProps = {
  fen: string;
  depth: number;
  variations: number;
  movetime: number;
  /**
   * fix U: false for a terminal (checkmate/stalemate/draw) position — the
   * worker stays warm but is told not to search it, since Stockfish answers
   * one with `bestmove (none)` and no usable score anyway.
   */
  searchEnabled: boolean;
  onResult: (
    variation: number,
    movesSAN: string[],
    baseTurn: 'w' | 'b',
    score: string,
    isMate: boolean,
    raw: string,
    searchedFen?: string,
  ) => void;
  onBestMove: (uci: string, searchedFen: string) => void;
};

export function useEngine(): EngineProps {
  const mode = useAnalysis(s => s.mode);
  const assist = mode === 'assist';
  const engineStatus = useEngineStatus(s => s.status);
  const viewPly = useV2Ui(s => s.viewPly);
  // D3: assist ignores the browsed ply — always the LIVE position
  const mainlineFen = useLiveGame(s =>
    analysisFenAtPly(s, assist ? null : viewPly),
  );
  const livePly = useLiveGame(s => analysisPly(s, assist ? null : viewPly));
  // the branch's tip when one is open (F-C / C3) — `ply` stays the browsed
  // ply either way, since it is only the `evalsByPly` key and a branch
  // position never writes one (`applyResult`'s own `branch === null` guard).
  // Read unconditionally (branch is always null in assist mode — the board
  // override is inert there, `stage/BoardFrame/model.ts`) and simply ignored
  // by `fen` below, rather than a second selector shape.
  const branchTip = useAnalysis(selectBranchTip);
  // fix L / L1: the HEAD of the finish-then-advance queue, while assisting —
  // ONE object subscription, identity stable until a shift (`settleAssist`),
  // so this never re-renders for the live game ticking underneath it.
  const head = useAnalysis(s => s.assistQueue[0]);
  const fen = assist ? head?.fen ?? mainlineFen : branchTip ?? mainlineFen;
  const ply = assist ? head?.ply ?? livePly : livePly;
  const movetime = assist
    ? head?.budgetMs ?? ASSIST_MOVETIME_MS
    : ANALYSIS_MOVETIME_MS;
  // fix U: the SAME pure check `analysisStore.setPosition` runs on this
  // exact fen, computed independently (rather than read off the store) so
  // `searchEnabled` is correct on fen's own first render — no one-tick lag
  // waiting for that effect to commit.
  const terminal = useMemo(() => terminalVerdict(fen), [fen]);

  // the store follows the browsed (or branched) position; the guard inside
  // `applyResult` then drops anything still in flight for the one before it
  useEffect(() => {
    useAnalysis.getState().setPosition(fen, ply);
  }, [fen, ply]);

  // fix L / L1: every live position is OFFERED to the queue while assisting
  // — the HEAD (above) is what actually reaches the engine, so a bot's
  // near-instant reply queues up behind the search already running instead
  // of cutting it short.
  useEffect(() => {
    if (assist) {
      useAnalysis.getState().enqueueAssist(mainlineFen, livePly);
    }
  }, [assist, mainlineFen, livePly]);

  // WATCHDOG: covers a crashed/re-initialising worker, which would otherwise
  // leave the head's ply with no chip and no way to ever advance the queue.
  // fix U: a mated/stalemated head no longer needs this to settle it — it is
  // never searched at all (`searchEnabled` below) — but the watchdog still
  // fires for one regardless (same `budgetMs + grace`), so a bug in the
  // terminal check fails safe into "no chip, queue advances" rather than a
  // stuck head. Gated on `engineStatus === 'ready'` (review fix, 2026-10-06):
  // a cold worker's own init/handshake can outlast the budget + grace window,
  // and arming from mount would settle the FIRST head with no `lines` at all
  // — in the deps array so the timer starts the moment readiness arrives,
  // not merely at mount.
  useEffect(() => {
    if (!assist || !head || engineStatus !== 'ready') {
      return undefined;
    }
    const timer = setTimeout(() => {
      useAnalysis.getState().settleAssist(head.fen, 'watchdog');
    }, head.budgetMs + ASSIST_SETTLE_GRACE_MS);
    return () => clearTimeout(timer);
  }, [assist, head, engineStatus]);

  const onResult = useCallback(
    (
      variation: number,
      movesSAN: string[],
      baseTurn: 'w' | 'b',
      score: string,
      isMate: boolean,
      raw: string,
      searchedFen?: string,
    ) => {
      const value = Number(score) || 0;
      const depthMatch = /\bdepth (\d+)\b/.exec(raw);
      useAnalysis.getState().applyResult(
        variation,
        {
          san: movesSAN,
          // white-POV, once, at the edge
          score: baseTurn === 'b' ? -value : value,
          isMate,
          depth: depthMatch ? Number(depthMatch[1]) : 0,
        },
        searchedFen ?? '',
      );
    },
    [],
  );

  const onBestMove = useCallback(
    (uci: string, searchedFen: string) => {
      useAnalysis.getState().setBestMove(uci, searchedFen);
      // fix L / L1: the head's own search is what a `bestmove` settles
      if (assist) {
        useAnalysis.getState().settleAssist(searchedFen, 'bestmove');
      }
    },
    [assist],
  );

  return {
    fen,
    depth: assist ? ASSIST_DEPTH : ANALYSIS_DEPTH,
    variations: assist ? ASSIST_LINES : ANALYSIS_LINES,
    movetime,
    searchEnabled: !terminal,
    onResult,
    onBestMove,
  };
}

export default useEngine;
